import { useEffect, useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Input, MaskedInput, Modal, useToast } from '@koyomi/ui';
import { isValidTaxId } from '@koyomi/shared';
import { api, ApiError } from '../../lib/api.ts';
import { formatMoney, useSubscription, type SubscriptionDto } from '../../lib/queries.ts';
import { useAuthStore } from '../../app/auth-store.ts';

export type Pix = { encodedImage: string; payload: string; expiresAt: string | null; entryId: string; amount: number; dueDate: string };
type CheckoutResult = { method: 'PIX' | 'CARD'; card: { last4: string; brand: string } | null; pix: Pix | null; entry: { id: string; amount: number; dueDate: string; status: string; invoiceUrl: string | null } | null };

function luhn(n: string): boolean { let s = 0, alt = false; for (let i = n.length - 1; i >= 0; i--) { let d = Number(n[i]); if (alt) { d *= 2; if (d > 9) d -= 9; } s += d; alt = !alt; } return n.length >= 13 && n.length <= 19 && s % 10 === 0; }
function parseExpiry(v: string): { month: string; year: string } | null {
  const m = v.replace(/\s/g, '').match(/^(\d{2})\/?(\d{2}|\d{4})$/);
  if (!m) return null;
  const month = m[1]!; const year = m[2]!.length === 2 ? `20${m[2]}` : m[2]!;
  if (Number(month) < 1 || Number(month) > 12) return null;
  if (new Date(Number(year), Number(month), 0) < new Date()) return null;
  return { month, year };
}
const fmtDue = (d: string) => d.split('-').reverse().join('/');
const isPaid = (s?: string) => s === 'CONFIRMED' || s === 'RECEIVED';

/** Enquanto o QR está na tela, recarrega a assinatura a cada 5 s até a cobrança constar como paga (o webhook do Asaas muda o status). */
function useEntryPaid(entryId: string | null, active: boolean): boolean {
  const qc = useQueryClient();
  const sub = useSubscription();
  const businessId = useAuthStore((s) => s.businessId);
  useEffect(() => {
    if (!active || !entryId) return;
    const id = setInterval(() => { void qc.invalidateQueries({ queryKey: [businessId, 'subscription'] }); }, 5000);
    return () => clearInterval(id);
  }, [active, entryId, qc, businessId]);
  return isPaid(sub.data?.entries.find((e) => e.id === entryId)?.status);
}

function PixView({ pix, paid }: { pix: Pix; paid: boolean }) {
  const { t, i18n } = useTranslation(['config', 'common']);
  const toast = useToast();
  const copy = async () => { try { await navigator.clipboard.writeText(pix.payload); toast.push(t('config:plan.checkout.copied'), 'success'); } catch { /* sem clipboard: o campo abaixo seleciona ao focar */ } };
  if (paid) return <div className="checkout__paid" role="status">{t('config:plan.checkout.paid')}</div>;
  return (
    <div className="k-stack" style={{ gap: 12 }}>
      <p className="k-muted" style={{ margin: 0 }}>{t('config:plan.checkout.pixSub')}</p>
      <div className="checkout__qr"><img src={`data:image/png;base64,${pix.encodedImage}`} alt="QR Pix" width={220} height={220} /></div>
      <strong style={{ textAlign: 'center' }}>{t('config:plan.checkout.amountDue', { amount: formatMoney(pix.amount, i18n.language), date: fmtDue(pix.dueDate) })}</strong>
      <textarea className="checkout__payload" readOnly value={pix.payload} rows={3} aria-label="Pix copia e cola" onFocus={(e) => e.currentTarget.select()} />
      <Button onClick={copy}>{t('config:plan.checkout.copy')}</Button>
      <span className="k-muted k-row" style={{ fontSize: 13, gap: 8 }}><span className="k-spinner" /> {t('config:plan.checkout.waiting')}</span>
    </div>
  );
}

/** "Pagar fatura (Pix)": QR de uma cobrança em aberto (mensalidade, pacote ou ajuste). */
export function PixModal({ entryId, onClose }: { entryId: string; onClose: () => void }) {
  const { t } = useTranslation(['config', 'common']);
  const q = useQuery({ queryKey: ['pix', entryId], queryFn: () => api<Pix>(`/business/billing/entries/${entryId}/pix`), retry: false, staleTime: 60_000 });
  const paid = useEntryPaid(entryId, !!q.data);
  return (
    <Modal open onClose={onClose} title={t('config:plan.checkout.pixTitle')} size="sm" footer={<Button onClick={onClose}>{t('common:actions.close')}</Button>}>
      {q.isLoading && <span className="k-spinner" />}
      {q.isError && <p className="k-field__error" role="alert">{q.error instanceof ApiError ? q.error.message : t('common:errors.INTERNAL')}</p>}
      {q.data && <PixView pix={q.data} paid={paid} />}
    </Modal>
  );
}

/**
 * Checkout transparente: CPF/CNPJ + Pix ou cartão, desde o primeiro dia do teste.
 * Cartão: tokenizado no Asaas, cobra a mensalidade em aberto agora e fica para os meses seguintes. Pix: QR desta fatura.
 */
export function CheckoutModal({ d, onClose }: { d: SubscriptionDto; onClose: () => void }) {
  const { t, i18n } = useTranslation(['config', 'common']);
  const qc = useQueryClient();
  const businessId = useAuthStore((s) => s.businessId);
  const [method, setMethod] = useState<'PIX' | 'CARD'>(d.billing.method === 'CARD' ? 'CARD' : 'PIX');
  const [f, setF] = useState({ taxId: d.billing.taxId ?? '', postalCode: d.billing.postalCode ?? '', addressNumber: d.billing.addressNumber ?? '', phone: d.billing.phone ?? '', holderName: '', number: '', expiry: '', ccv: '' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [rootError, setRootError] = useState<string | null>(null);
  const [result, setResult] = useState<CheckoutResult | null>(null);
  const paid = useEntryPaid(result?.pix?.entryId ?? null, !!result?.pix);
  const set = (k: keyof typeof f) => (e: ChangeEvent<HTMLInputElement>) => setF((p) => ({ ...p, [k]: e.target.value }));
  const openEntry = d.entries.find((e) => e.type === 'PLAN' && (e.status === 'OPEN' || e.status === 'OVERDUE'));
  const amount = openEntry?.amount ?? d.plan?.price ?? 0;

  const submit = async () => {
    const errs: Record<string, string> = {};
    const required = t('config:plan.checkout.errors.required');
    if (!isValidTaxId(f.taxId)) errs.taxId = t('config:plan.checkout.errors.taxId');
    let card: { holderName: string; number: string; expiryMonth: string; expiryYear: string; ccv: string } | undefined;
    if (method === 'CARD') {
      if (!/^\d{8}$/.test(f.postalCode.replace(/\D/g, ''))) errs.postalCode = required;
      if (!f.addressNumber.trim()) errs.addressNumber = required;
      if (f.phone.replace(/\D/g, '').length < 10) errs.phone = required;
      if (f.holderName.trim().length < 2) errs.holderName = required;
      const num = f.number.replace(/\D/g, '');
      if (!luhn(num)) errs.number = t('config:plan.checkout.errors.card');
      const exp = parseExpiry(f.expiry);
      if (!exp) errs.expiry = t('config:plan.checkout.errors.expiry');
      if (!/^\d{3,4}$/.test(f.ccv)) errs.ccv = t('config:plan.checkout.errors.ccv');
      if (exp) card = { holderName: f.holderName.trim(), number: num, expiryMonth: exp.month, expiryYear: exp.year, ccv: f.ccv };
    }
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setBusy(true); setRootError(null);
    try {
      const r = await api<CheckoutResult>('/business/billing/checkout', { body: { method, taxId: f.taxId, postalCode: f.postalCode || undefined, addressNumber: f.addressNumber || undefined, phone: f.phone || undefined, card } });
      setResult(r);
      setF((p) => ({ ...p, number: '', ccv: '' })); // dados do cartão não ficam na memória da página
      await Promise.all([qc.invalidateQueries({ queryKey: [businessId, 'subscription'] }), qc.invalidateQueries({ queryKey: [businessId, 'business'] })]);
    } catch (err) {
      setRootError(err instanceof ApiError ? err.message : t('common:errors.INTERNAL'));
    } finally { setBusy(false); }
  };

  const title = d.billing.method ? t('config:plan.checkout.changeTitle') : t('config:plan.checkout.title');
  if (result) {
    return (
      <Modal open onClose={onClose} title={title} size="sm" footer={<Button variant="primary" onClick={onClose}>{t('common:actions.close')}</Button>}>
        {result.method === 'CARD' && result.card && <div className="checkout__paid" role="status">{isPaid(result.entry?.status) ? t('config:plan.checkout.cardDonePaid', { amount: formatMoney(result.entry?.amount ?? amount, i18n.language), brand: result.card.brand, last4: result.card.last4 }) : t('config:plan.checkout.cardDone', { brand: result.card.brand, last4: result.card.last4 })}</div>}
        {result.method === 'PIX' && (result.pix ? <PixView pix={result.pix} paid={paid} /> : <div className="checkout__paid" role="status">{t('config:plan.checkout.pixSet')}</div>)}
      </Modal>
    );
  }
  return (
    <Modal open onClose={onClose} title={title} size="md" footer={<><Button onClick={onClose}>{t('common:actions.cancel')}</Button><Button variant="primary" loading={busy} onClick={submit}>{method === 'PIX' ? t('config:plan.checkout.submitPix') : openEntry ? t('config:plan.checkout.submitCard', { amount: formatMoney(amount, i18n.language) }) : t('config:plan.checkout.submitCardOnly')}</Button></>}>
      <div className="k-stack" style={{ gap: 14 }}>
        <p className="k-muted" style={{ margin: 0 }}>{t('config:plan.checkout.intro', { plan: d.plan?.name ?? '', price: formatMoney(d.plan?.price ?? 0, i18n.language) })}</p>
        <div className="checkout__methods" role="radiogroup">
          {(['PIX', 'CARD'] as const).map((m) => (
            <div key={m} role="radio" aria-checked={method === m} tabIndex={0} className={`plan__offer checkout__method ${method === m ? 'plan__offer--selected' : ''}`} onClick={() => setMethod(m)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setMethod(m); } }}>
              <strong>{t(`config:plan.checkout.${m === 'PIX' ? 'pix' : 'card'}`)}</strong>
              <span className="k-muted" style={{ fontSize: 13 }}>{t(`config:plan.checkout.${m === 'PIX' ? 'pixDesc' : 'cardDesc'}`)}</span>
            </div>
          ))}
        </div>
        <MaskedInput mask="cpfCnpj" label={t('config:plan.checkout.taxId')} hint={t('config:plan.checkout.taxIdHint')} autoComplete="off" value={f.taxId} onChange={set('taxId')} error={errors.taxId} />
        {method === 'CARD' && (
          <>
            <div className="k-section-label">{t('config:plan.checkout.holderInfo')}</div>
            <div className="k-grid k-grid--stack" style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
              <MaskedInput mask="cep" label={t('config:plan.checkout.postalCode')} value={f.postalCode} onChange={set('postalCode')} error={errors.postalCode} />
              <Input label={t('config:plan.checkout.addressNumber')} value={f.addressNumber} onChange={set('addressNumber')} error={errors.addressNumber} />
              <MaskedInput mask="phone" label={t('config:plan.checkout.phone')} value={f.phone} onChange={set('phone')} error={errors.phone} />
            </div>
            <div className="k-section-label">{t('config:plan.checkout.cardInfo')}</div>
            <Input label={t('config:plan.checkout.holderName')} autoComplete="cc-name" value={f.holderName} onChange={set('holderName')} error={errors.holderName} />
            <MaskedInput mask="card" label={t('config:plan.checkout.number')} value={f.number} onChange={set('number')} error={errors.number} />
            <div className="k-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
              <MaskedInput mask="expiry" label={t('config:plan.checkout.expiry')} value={f.expiry} onChange={set('expiry')} error={errors.expiry} />
              <MaskedInput mask="cvv" label={t('config:plan.checkout.ccv')} value={f.ccv} onChange={set('ccv')} error={errors.ccv} />
            </div>
            <p className="k-muted" style={{ fontSize: 12, margin: 0 }}>{t('config:plan.checkout.secure')}</p>
          </>
        )}
        {rootError && <div className="k-field__error" role="alert">{rootError}</div>}
      </div>
    </Modal>
  );
}
